import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Printer, AlertCircle, BarChart3, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ReportHeader } from '@/components/report/report-header';
import { ReportKpis } from '@/components/report/report-kpis';
import { ReportSegments } from '@/components/report/report-segments';
import { ReportSkuTable } from '@/components/report/report-sku-table';
import { ReportTrendChart } from '@/components/report/report-trend-chart';
import { ReportTopSkusChart } from '@/components/report/report-top-skus-chart';
import { PeriodFilter } from '@/components/report/period-filter';
import { ShareDialog } from '@/components/report/share-dialog';
import { useCurrentDate } from '@/hooks/use-current-date';
import { getBrandReport, getAvailablePeriods } from '@/lib/api';
import type { AvailablePeriods, BrandReport, BrandStat } from '@/lib/api';

export interface ReportRouteProps {
  brands: BrandStat[];
}

const FOOTER_DATE = new Intl.DateTimeFormat('vi-VN', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'Asia/Ho_Chi_Minh',
});

const MONTH_RANGE_FORMATTER = new Intl.DateTimeFormat('vi-VN', {
  month: '2-digit',
  year: 'numeric',
  timeZone: 'Asia/Ho_Chi_Minh',
});

function formatMonthRange(months: string[]): string | undefined {
  if (months.length < 2) return months[0];
  const first = months[0];
  const last = months[months.length - 1];
  if (!first || !last) return undefined;
  const fmt = (m: string) => {
    const [y, mo] = m.split('-');
    return `${mo}/${y}`;
  };
  return `${fmt(first)} → ${fmt(last)}`;
}

export default function ReportRoute({ brands }: ReportRouteProps) {
  const { brand: brandSlug } = useParams<{ brand: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const period = searchParams.get('period') ?? 'all';
  const compare = searchParams.get('compare') ?? '';

  const [report, setReport] = useState<BrandReport | null>(null);
  const [available, setAvailable] = useState<AvailablePeriods | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const now = useCurrentDate();
  const [shareOpen, setShareOpen] = useState(false);

  const fallbackBrandName = useMemo(() => {
    const found = brands.find((b) => b.brand.toLowerCase() === brandSlug?.toLowerCase());
    return found?.brand ?? brandSlug?.toUpperCase() ?? 'Unknown';
  }, [brands, brandSlug]);

  // Load available periods once
  useEffect(() => {
    let cancelled = false;
    getAvailablePeriods()
      .then((p) => !cancelled && setAvailable(p))
      .catch((err) => console.error('Periods load failed:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  // Load report when brand or period or compare changes
  useEffect(() => {
    if (!brandSlug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    document.title = `Báo cáo ${fallbackBrandName} — GearVN`;

    getBrandReport(brandSlug, { period, compare: compare || undefined })
      .then((data) => {
        if (cancelled) return;
        setReport(data);
        document.title = `Báo cáo ${data.brand} — GearVN`;
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
      document.title = 'Monitor Dashboard — GearVN';
    };
  }, [brandSlug, fallbackBrandName, period, compare]);

  const handlePeriodChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('period');
    else next.set('period', value);
    // If new period == compare, clear compare to avoid invalid state
    if (next.get('compare') === value) next.delete('compare');
    setSearchParams(next, { replace: true });
  };

  const handleCompareChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value) next.delete('compare');
    else next.set('compare', value);
    setSearchParams(next, { replace: true });
  };

  const focalSkus = useMemo(
    () =>
      (report?.monitors ?? [])
        .filter((m) => m.is_focal === 1)
        .sort((a, b) => a.name.localeCompare(b.name, 'vi')),
    [report]
  );

  const allSkus = useMemo(
    () =>
      [...(report?.monitors ?? [])].sort((a, b) => {
        if (a.is_focal !== b.is_focal) return b.is_focal - a.is_focal;
        return a.name.localeCompare(b.name, 'vi');
      }),
    [report]
  );

  const monthRange = useMemo(() => (report ? formatMonthRange(report.months) : undefined), [report]);

  const handlePrint = () => window.print();

  const compareData = report?.compare ?? null;

  return (
    <div className="relative mx-auto max-w-5xl">
      <div className="print-watermark hidden">CONFIDENTIAL</div>

      {/* Action bar — hidden when printing */}
      <div className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b bg-background/80 px-6 py-3 backdrop-blur print:hidden">
        <Button asChild variant="ghost" size="sm">
          <Link to="/">
            <ArrowLeft className="h-4 w-4" />
            Quay lại Product Map
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handlePrint} disabled={loading || !report}>
            <Printer className="h-4 w-4" />
            In / Xuất PDF
          </Button>
          <Button size="sm" onClick={() => setShareOpen(true)} disabled={loading || !report}>
            <Share2 className="h-4 w-4" />
            Chia sẻ với hãng
          </Button>
        </div>
      </div>

      {report && <ShareDialog open={shareOpen} onOpenChange={setShareOpen} brand={report.brand} />}

      <div className="space-y-6 p-6">
        {error && (
          <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <div>
              <div className="font-semibold text-destructive">Lỗi tải báo cáo</div>
              <div className="mt-1 text-muted-foreground">{error}</div>
            </div>
          </div>
        )}

        {loading ? (
          <ReportSkeleton />
        ) : !report ? (
          <div className="rounded-lg border border-dashed bg-muted/30 p-12 text-center">
            <h2 className="text-lg font-semibold">Không có dữ liệu cho hãng "{fallbackBrandName}"</h2>
            <Button asChild className="mt-4" size="sm">
              <Link to="/">
                <ArrowLeft className="h-4 w-4" />
                Về Product Map
              </Link>
            </Button>
          </div>
        ) : (
          <>
            <ReportHeader brand={report.brand} monthRange={monthRange} date={now} />

            {/* Period filter — admin only, hidden on print */}
            <div className="print:hidden">
              <PeriodFilter
                available={available}
                period={period}
                compare={compare}
                onPeriodChange={handlePeriodChange}
                onCompareChange={handleCompareChange}
              />
            </div>

            <ReportKpis
              totals={report.totals}
              periodLabel={report.period.label}
              compare={
                compareData
                  ? { totals: compareData.totals, periodLabel: compareData.period.label }
                  : null
              }
            />

            <section className="rounded-lg border bg-card p-4 shadow-sm print:rounded-md print:shadow-none print-no-break">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h2 className="flex items-center gap-2 text-sm font-semibold">
                    <BarChart3 className="h-4 w-4 text-primary" />
                    {report.months.length === 1
                      ? `Top SKU theo doanh thu — ${report.period.label}`
                      : compareData
                      ? `Doanh thu: ${report.period.label} vs ${compareData.period.label}`
                      : 'Xu hướng Doanh thu & Lợi nhuận'}
                  </h2>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    {report.months.length === 1
                      ? `${report.brand} · sắp xếp doanh thu giảm dần`
                      : compareData
                      ? `${report.months.length} vs ${compareData.months.length} tháng · ${report.brand}`
                      : `${report.months.length} tháng · ${report.brand}`}
                  </p>
                </div>
                <div className="text-right text-[11px] text-muted-foreground">
                  Cập nhật: {MONTH_RANGE_FORMATTER.format(now)}
                </div>
              </div>
              {report.months.length === 1 ? (
                <ReportTopSkusChart monitors={report.monitors} />
              ) : (
                <ReportTrendChart
                  data={report.trend}
                  compareData={compareData?.trend}
                  currentLabel={report.period.label}
                  compareLabel={compareData?.period.label}
                />
              )}
            </section>

            <ReportSegments
              segments={report.segments}
              compareSegments={compareData?.segments}
              compareLabel={compareData?.period.label}
            />

            <div className="print-break-before pt-2">
              <ReportSkuTable title="Sản phẩm Tập trung (Focal Point)" monitors={focalSkus} />
            </div>

            <ReportSkuTable title="Toàn bộ SKU Active" monitors={allSkus} />

            <footer className="flex items-center justify-between border-t pt-3 text-[10px] text-muted-foreground">
              <span>
                GearVN × {report.brand} — Confidential | {FOOTER_DATE.format(now)}
              </span>
              <span>
                v0.1.0 · {report.total_skus} SKUs · {report.focal_count} focal · {report.period.label}
              </span>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}

function ReportSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between border-b pb-4">
        <div className="space-y-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-7 w-72" />
          <Skeleton className="h-3 w-40" />
        </div>
        <Skeleton className="h-12 w-32" />
      </div>
      <Skeleton className="h-12 rounded-lg" />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-64 rounded-lg" />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-28 rounded-lg" />
        ))}
      </div>
    </div>
  );
}
